// Human labels for the per-media persistence failure stages the backend stamps
// (UpdateMediaItemMetadata: persistence_error_stage) and echoes inside render
// error messages. Keep in sync with contentgen-go-backend store persistence.

export const PERSISTENCE_STAGE_LABELS: Record<string, string> = {
  media_row_upsert: 'Saving media record',
  store_bytes_upload: 'Uploading to storage',
  file_url_update: 'Finalizing file link',
  // The provider call itself was rejected — an RAI/safety block, a quota
  // refusal, a timeout. It is the stage a failed REGENERATE almost always
  // reports (internal/api/media regenerateVariants.go stamps it as the fallback
  // when the generator never got far enough to name a persistence stage).
  provider_generation: 'Generating with the provider',
};

/**
 * Stages whose id must NOT be matched as a SUBSTRING of an error message by
 * `friendlyRenderError`.
 *
 * That scan exists for persistence errors, whose messages echo the stage id and
 * carry nothing else a reader can use. `provider_generation` is the opposite
 * case: its message IS the useful part ("video blocked by Vertex safety
 * filter: … (support code 15236754)"), so collapsing it to the label would
 * throw away the only sentence that explains anything. The label still renders
 * — separately, via `persistenceStageLabel`.
 */
const OPAQUE_STAGE_IDS = new Set(['provider_generation']);

/** Friendly label for a persistence stage id, falling back to the raw id. */
export const persistenceStageLabel = (stage: string): string =>
  PERSISTENCE_STAGE_LABELS[stage] ?? stage;

/**
 * Turn a raw render error into a reader-friendly message. When the message
 * carries a known persistence stage id, surface its label; otherwise pass the
 * message through (or a generic fallback for an empty error).
 */
export const friendlyRenderError = (raw: string | undefined): string => {
  const message = (raw || '').trim();
  if (!message) return 'Something went wrong. Try again.';
  for (const [stage, label] of Object.entries(PERSISTENCE_STAGE_LABELS)) {
    if (OPAQUE_STAGE_IDS.has(stage)) continue;
    if (message.includes(stage)) return label;
  }
  return message;
};

export interface PersistenceFailure {
  /** The provider's / store's own words, already truncated to 500 chars. */
  message: string;
  /** Stage id, e.g. `provider_generation`. Empty when the backend stamped none. */
  stage: string;
}

/** Shown when the error is present but not readable — never `[object Object]`. */
export const STRUCTURED_FAILURE_TEXT = 'Generation failed';

/**
 * The backend stamps `persistence_error` as a string today. Should it ever
 * stamp a structured error instead — a shape, a list of violations — `String()`
 * would render the literal text `[object Object]` on the operator's card, which
 * is worse than saying nothing: it reads as a bug in our UI rather than as a
 * refusal by the provider. Anything that is not a string is reported as a
 * failure without words, and absence stays absence (the generic line).
 */
const readableDetail = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  return STRUCTURED_FAILURE_TEXT;
};

/**
 * A media item's OWN bytes failed, read off its metadata.
 *
 * `persistence_status: "failed"` is the flag; `persistence_error` carries the
 * reason and `persistence_error_stage` the step. Deliberately distinct from
 * `readRegenerateFailure` (regenerateFailure.ts), which describes a failed
 * REGENERATION of an item whose own bytes are fine — collapsing the two would
 * make a still-playing clip read as broken.
 *
 * A failed status with no words at all is still a failure: the flag is what
 * the UI must not swallow, and an empty message renders as the generic line
 * `friendlyRenderError` supplies.
 */
export const readPersistenceFailure = (metadata: unknown): PersistenceFailure | null => {
  if (!metadata || typeof metadata !== 'object') return null;
  const record = metadata as Record<string, unknown>;
  if (String(record.persistence_status ?? '').trim() !== 'failed') return null;
  return {
    message: readableDetail(record.persistence_error),
    stage: typeof record.persistence_error_stage === 'string'
      ? record.persistence_error_stage.trim()
      : '',
  };
};

/** One line: which step failed, and why. */
export const persistenceFailureText = (failure: PersistenceFailure): string => {
  const detail = friendlyRenderError(failure.message);
  return failure.stage ? `${persistenceStageLabel(failure.stage)} failed — ${detail}` : detail;
};
